import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  User,
  UserTypes,
} from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { UIComponentsTheme } from 'app/ui-components/ui-components-theme.enum';
import { EditProfileComponent } from 'app/user/edit-profile/edit-profile.component';
import { UserService } from 'app/user/user.service';

import { ListUsersType } from './list-types.enum';
import { SortOrder } from './sort-order.enum';
import { UserListTabView } from './tab-view.enum';
import { UserListService } from './user-list.service';
import { IUser } from '../user-list.model';
import { UserWeb } from '../user.model';
import { IUserList } from '../users-list.model';

@Component({
  selector: 'cc-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  providers: [],
})
export class UserListComponent {
  @Input() userList: IUserList;
  @Input() userType: UserTypes;
  @Input() hasCurrentOrSubFoldersToggle: boolean;
  @Input() currentUserId: string;
  @Input() isLoading = true;
  @Input() canRemoveUser: boolean;
  @Input() canEditProfile: boolean;
  @Input() showPermissionToggle: boolean;
  @Input() canUpdateUserPermission: boolean;
  @Input() permissionOptions: string[];

  @Output() refreshUsers: EventEmitter<void> = new EventEmitter();
  @Output() remove: EventEmitter<User> = new EventEmitter();
  @Output() onUpdatePermission = new EventEmitter<UserWeb>();
  @Output() onUpdateTabView = new EventEmitter<UserListTabView>();

  readonly userListTabView = UserListTabView;
  readonly userListToggleTheme = UIComponentsTheme.Grey2;
  readonly sortOrder = SortOrder;
  readonly defaultSortHeader = 'firstName';
  readonly defaultSortOrder = SortOrder.ASC;
  readonly PendingUsers = ListUsersType.PendingUsers;
  readonly ActiveUsers = ListUsersType.ActiveUsers;
  readonly activeUsersTableHeaders = [
    { name: 'firstName', label: 'First Name' },
    { name: 'lastName', label: 'Last Name' },
    { name: 'email', label: 'Email' },
  ];

  pendingUsersTableHeaders = [
    { name: 'inviteStatus', label: 'Email Invite Status' },
    { name: 'action', label: 'Action' },
    { name: 'email', label: 'Email' },
  ];

  sortingColumn = {
    [this.PendingUsers]: this.defaultSortHeader,
    [this.ActiveUsers]: this.defaultSortHeader,
  };

  currentSortOrder = {
    [this.PendingUsers]: this.defaultSortOrder,
    [this.ActiveUsers]: this.defaultSortOrder,
  };

  currentFolderActiveUsers = {
    [UserTypes.Contributor]: 'contributorsNoActiveUsers',
    [UserTypes.Visitor]: 'visitorsNoActiveUsers',
  };

  currentFolderPendingUsers = {
    [UserTypes.Contributor]: 'contributorsNoPendingUsers',
    [UserTypes.Visitor]: 'visitorsNoPendingUsers',
  };

  subFolderActiveUsers = {
    [UserTypes.Contributor]: 'contributorsSubFolderNoActiveUsers',
    [UserTypes.Visitor]: 'visitorsSubFolderNoActiveUsers',
  };

  subFolderPendingUsers = {
    [UserTypes.Contributor]: 'contributorsSubFolderNoPendingUsers',
    [UserTypes.Visitor]: 'visitorsSubFolderNoPendingUsers',
  };

  checkedUsers = {
    pendingUsers: new Set(),
    activeUsers: new Set(),
  };

  sortedList = {
    activeUsers: [],
    pendingUsers: [],
  };

  defaultList = {
    activeUsers: [],
    pendingUsers: [],
  };

  tabView = UserListTabView.CurrentFolder;

  searchTerm = '';
  isTeamControllerOrAbove = false;
  ongoingUserList: IUser[];
  emptyUserListMessages: { [key in UserListTabView]: { activeUsers: string; pendingUsers: string } };

  constructor(
    private userListService: UserListService,
    protected readonly confirmService: ConfirmService,
    protected readonly modal: NgbModal,
    protected readonly i18nService: TmpI18NService,
    protected readonly segmentService: SegmentService,
    protected readonly userService: UserService,
  ) {}

  ngOnInit() {
    this.setEmptyUserListMessages();
    this.ongoingUserList = this.userList?.[this.tabView];
  }

  ngOnChanges(changes: SimpleChanges) {
    const userList = changes?.userList?.currentValue;
    if (userList) {
      this.updateUserList(userList[this.tabView]);
      this.ongoingUserList = this.userList?.[this.tabView];
    }
  }

  get isCurrentFolderView() {
    return this.tabView === this.userListTabView.CurrentFolder;
  }

  updateUserList(userList: IUser[]) {
    if (userList?.length) {
      this.loadUsers(userList);
      this.hideRestrictedFields();
    } else {
      this.defaultList.activeUsers = [];
      this.defaultList.pendingUsers = [];
    }
  }

  hideRestrictedFields() {
    if (!this.canRemoveUser) {
      this.pendingUsersTableHeaders = this.pendingUsersTableHeaders.filter(({ name }) => name !== 'action');
    }
  }

  loadUsers(userList: IUser[]) {
    let loadedList = this.userListService.sortListByDirection(
      userList,
      this.defaultSortHeader,
      this.defaultSortOrder,
    );
    this.setDefaultList(loadedList);
    loadedList = this.userListService.filterList(loadedList, this.searchTerm);
    this.setSortedList(loadedList);
  }

  onSort(column: string, list: ListUsersType): void {
    if (this.sortingColumn[list] !== column) {
      this.currentSortOrder[list] = this.defaultSortOrder;
    } else {
      this.currentSortOrder[list] =
        this.currentSortOrder[list] === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    }
    this.sortingColumn[list] = column;
    const sortedList = this.userListService.sortListByDirection(
      this.ongoingUserList,
      this.sortingColumn[list],
      this.currentSortOrder[list],
    );

    this.sortedList[list] = sortedList;
  }

  editProfile() {
    const checkedUser = Array.from(this.checkedUsers.activeUsers).concat(
      Array.from(this.checkedUsers.pendingUsers),
    )[0];

    const selectedUser = this.ongoingUserList.find((user) => user.id === checkedUser);

    void EventNotifierService.notify(new DashpivotEvent(EventTypes.UserProfileViewed), this.segmentService);
    const modalRef = this.modal.open(EditProfileComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.isAdmin = true;
    modalRef.componentInstance.user = { id: selectedUser.id };
    modalRef.result.then(() => this.refreshUsers.emit()).catch(() => {});
  }

  removeUsers() {
    const checkedUsers = Array.from(this.checkedUsers.activeUsers).concat(
      Array.from(this.checkedUsers.pendingUsers),
    );

    const usersToRemove = this.ongoingUserList.filter((user) => checkedUsers.includes(user.id)) as UserWeb[];
    const i18nMessage = usersToRemove.length > 1 ? 'removeUsersFromTeam' : 'removeUserFromTeam';

    this.confirmService
      .confirmDelete(i18nMessage, {
        confirmButtonText: this.i18nService.getMessage('confirmRemove'),
      })
      .then(() => Promise.all(usersToRemove.map((user) => this.remove.emit(user))))
      .then(() => {
        return this.clearCheckedUsers();
      })
      .catch(() => {});
  }

  onCheck(user: IUser, list: ListUsersType) {
    if (this.checkedUsers[list].has(user.id)) {
      this.checkedUsers[list].delete(user.id);
    } else {
      this.checkedUsers[list].add(user.id);
    }
  }

  onCheckAll(list: ListUsersType) {
    if (this.checkedUsers[list].size === this.sortedList[list].length) {
      this.checkedUsers[list].clear();
    } else {
      this.sortedList[list].forEach((user) => {
        this.checkedUsers[list].add(user.id);
      });
    }
  }

  onSearch() {
    const sortedList = this.userListService.filterList(this.ongoingUserList, this.searchTerm);
    this.setSortedList(sortedList);
  }

  setDefaultList(usersList) {
    this.defaultList.activeUsers = usersList.filter(({ isActivated }) => isActivated);
    this.defaultList.pendingUsers = usersList.filter(({ isActivated }) => !isActivated);
  }

  setSortedList(usersList) {
    this.sortedList.activeUsers = usersList.filter(({ isActivated }) => isActivated);
    this.sortedList.pendingUsers = usersList.filter(({ isActivated }) => !isActivated);
  }

  clearCheckedUsers() {
    this.checkedUsers.activeUsers.clear();
    this.checkedUsers.pendingUsers.clear();
  }

  switchFolderToggle(tabView: UserListTabView) {
    this.tabView = tabView;
    this.ongoingUserList = this.userList?.[this.tabView];
    this.loadUsers(this.ongoingUserList);
    this.onUpdateTabView.emit(tabView);
  }

  setEmptyUserListMessages() {
    this.emptyUserListMessages = {
      currentFolder: {
        activeUsers: this.i18nService.getMessage(this.currentFolderActiveUsers[this.userType]),
        pendingUsers: this.i18nService.getMessage(this.currentFolderPendingUsers[this.userType]),
      },
      subFolder: {
        activeUsers: this.i18nService.getMessage(this.subFolderActiveUsers[this.userType]),
        pendingUsers: this.i18nService.getMessage(this.subFolderPendingUsers[this.userType]),
      },
    };
  }
}
